/*
 * The shared package's rules in Danish (S48, extended to the admin site
 * 2026-10-29) - line for line rules.en.ts, the numbers from the same constants
 * the rules are enforced with, so a changed limit changes both languages.
 */

import {
  ARTICLE_IMAGE_MAX_BYTES,
  ARTICLE_IMAGE_MAX_PER_ARTICLE,
  ATTACHMENT_FILE_TYPES,
  ATTACHMENT_MAX_BYTES,
  ATTACHMENT_MAX_PER_ARTICLE,
  RELEASE_NOTE_IMAGE_MAX_PER_NOTE,
  formatByteSize,
} from '@accotool-kb/shared';
import type { rulesEn } from './rules.en.js';

/** A size as the Danish dictionary writes one: a decimal comma. */
function size(bytes: number): string {
  return formatByteSize(bytes).replace('.', ',');
}

/** 'release note' or 'article', as the shared functions name the thing - in the definite form. */
function definite(noun: string): string {
  return noun === 'release note' ? 'udgivelsesnoten' : 'artiklen';
}

const dotted = ATTACHMENT_FILE_TYPES.map((type) => `.${type.extension}`);

const THEME_DESCRIPTIONS: Record<string, string> = {
  classic: 'Den blå, som vidensbasen blev leveret med.',
  amber: 'Varm orange, bygget på #F79410.',
  plum: 'Dyb violet.',
  graphite: 'Neutral koksgrå; farverne overlades til indholdet.',
};

export const rulesDa: typeof rulesEn = {
  slugRequirement: 'Kun små bogstaver a-z, cifre og bindestreger.',
  categoryNameRequirement: 'Et kategorinavn må ikke indeholde komma.',
  imageFileNameRequirement:
    'Små bogstaver a-z, cifre og bindestreger, med ét punktum og en filtype på 2-4 tegn - for eksempel connector-overview.png.',
  imageSizeLimit: `Billeder må højst fylde ${size(ARTICLE_IMAGE_MAX_BYTES)}.`,
  articleImageCountLimit:
    `En artikel kan højst have ${ARTICLE_IMAGE_MAX_PER_ARTICLE} billeder. ` +
    'Slet et, før du tilføjer et nyt, eller erstat et eksisterende.',
  releaseNoteImageCountLimit:
    `En udgivelsesnote kan højst have ${RELEASE_NOTE_IMAGE_MAX_PER_NOTE} billeder. ` +
    'Slet et, før du tilføjer et nyt, eller erstat et eksisterende.',
  attachmentFileTypes: `${dotted.slice(0, -1).join(', ')} og ${dotted[dotted.length - 1]}`,
  attachmentSizeLimit: `Vedhæftede filer må højst fylde ${size(ATTACHMENT_MAX_BYTES)}.`,
  attachmentCountLimit: `En artikel kan højst have ${ATTACHMENT_MAX_PER_ARTICLE} vedhæftede filer på sin liste.`,
  releaseNoteVersionRequirement:
    'Brug v og fire hele tal adskilt af punktummer, for eksempel v1.2.3.4 - uden foranstillede nuller og med højst 9 cifre i hvert.',
  siteIconSizeLimit: 'Ikonet må højst fylde 1 MB.',
  visibility: (visibility) => (visibility === 'private' ? 'Privat' : 'Offentlig'),
  themeDescription: (theme) => THEME_DESCRIPTIONS[theme] ?? '',
  publishingStatus: (status, noun = 'article') => {
    switch (status) {
      case 'live':
        return { label: 'Live', detail: 'Synlig for alle.' };
      case 'scheduled':
        return { label: 'Planlagt', detail: 'Udgivelsestidspunktet ligger ude i fremtiden.' };
      case 'hidden':
        return { label: 'Skjult', detail: 'Skjult for læserne, uanset hvad udgivelsesdatoen siger.' };
      case 'withdrawn':
        return {
          label: 'Trukket tilbage',
          detail:
            'Den udgivne tekst er trukket tilbage. Historikken er intakt, og der vises intet, før en revision gøres gældende igen.',
        };
      case 'unwritten':
        return {
          label: 'Uskrevet',
          detail: `Der er endnu ikke skrevet noget: ${definite(noun)} har ingen revision overhovedet.`,
        };
      default:
        return { label: String(status), detail: '' };
    }
  },
  articleStatus: (status, noun = 'article') => {
    switch (status) {
      case 'published':
        return { label: 'Udgivet', detail: `Udgivelsesdatoen er passeret, og ${definite(noun)} er synlig.` };
      case 'scheduled':
        return { label: 'Planlagt', detail: 'Bliver læsbar på udgivelsestidspunktet.' };
      case 'hidden':
        return { label: 'Skjult', detail: 'Skjult for alle undtagen administratorer.' };
      default:
        return { label: String(status), detail: '' };
    }
  },
};
